<template>
  <PartsPageNav :items="breadcrumbItems" modifier="product-detail-page-nav" />

  <main class="main_product-detail">
    <PartsLayoutCol2 :main-wrapper="false">
      <CommonsBuyee :input-tag-value="buyeeInputTagValue" />
      <OrganismsProductsDetail :product="product" :content="content" />
    </PartsLayoutCol2>

    <OrganismsSupport />
  </main>
</template>

<script setup lang="ts">
/**
 * 商品詳細。支給デザイン `product-detail/product<コード>.html` の `main.main_product-detail` に対応する。
 *
 * **サイドバーを持たず `col2-main-wrapper` も無い**（`main-wrapper` を `false`）。
 * 支給は `col2-wrapper` の直下が `sec_product-detail` で、`.container` は
 * その中（FV の下）から始まる（決定#53）。
 *
 * **本文データは商品別 const**（`src/consts/products/<コード>.ts`・決定#57）。
 * `useProductDetailContent` が `import.meta.glob` で解決するので、静的マップは持たない（決定#58）。
 *
 * **Buyee（海外購入代行）のタグと `501` の GTM 個別注入は旧実装から引き継ぐ。**
 * どちらも支給デザインには無いが、落とすと計測と海外導線が切れる。
 * Buyee のバナーは第三者スクリプトが後から中身を差し込む枠で、旧実装ではFVの中にあった。
 * 支給にはFV内に置き場所が無いため本文の先頭に出す（**表示位置は要デザイン確認**）。
 */
import type {
  BreadcrumbType,
  ProductType,
  ProductCategoryType,
  ProductDetailContentType,
} from '@/types/domains/index';
import { useRoute } from 'vue-router';
import { ProductPresenter } from '@/presenters/ProductPresenter';
import {
  PRODUCT_CONTENT_CONSTANTS,
  PRODUCT_CODE_TO_CATEGORY_ID,
} from '@/consts/ProductContentConst';

const route = useRoute();
const presenter = new ProductPresenter();
const code: string = route.params.id as string;
const product: ProductType.Product = await presenter.getProduct(code);

const { getContent } = useProductDetailContent();
const content: ProductDetailContentType.ProductDetailContent | null = await getContent(code);

// 代表5商品ぶんの const しか無い期間（〜8/12 の一括変換）は、残る33商品がここで止まる。
// 空のページを描くより、何が足りないかを言って止めるほうが気づける。
if (!content) {
  throw createError({
    statusCode: 500,
    statusMessage: 'Missing product detail content',
    message: `src/consts/products/${code}.ts が未作成です。scripts/design-to-const.mjs で生成してください。`,
    fatal: true,
  });
}

// カテゴリは `PRODUCT_CODE_TO_CATEGORY_ID` を正とする。API の値は旧3分類のままで、
// 食品（4）はこの上書きでしか出ない（`useProductContent.ts` と同じ解決）
const categoryId: number = PRODUCT_CODE_TO_CATEGORY_ID[code] ?? product.productContentsCategoryId;
const productTypeOfString: ProductCategoryType.Category =
  PRODUCT_CONTENT_CONSTANTS.CATEGORY_JAPANESE_NAME[categoryId];
// 中間階層はトップの商品タブへ飛ばす（決定#91）。タブの選択状態は `?category=` だけで決まる
const categorySlug: ProductCategoryType.CategoryEnglishName | undefined =
  PRODUCT_CONTENT_CONSTANTS.CATEGORY_ENGLISH_NAME[categoryId];
const breadcrumbItems: BreadcrumbType.Breadcrumb[] = [
  { text: 'トップ', href: '/' },
  {
    text: productTypeOfString,
    href: categorySlug ? `/?category=${categorySlug}#products` : null,
  },
  { text: product.name, href: null },
];

const buyeeInputTagValue: ComputedRef<string> = computed((): string => {
  switch (code) {
    case '501':
      return '001-501';
    case '558':
      return '005-559';
    case '559':
      return '005-559';
    default:
      return '';
  }
});

useSeoMeta({
  title: `${product.metaTitle ?? product.name} | オリエンタルバイオ公式オンラインショップ`,
  description: product.metaDescription,
  keywords: product.metaKeyword,
});

if (useRuntimeConfig().public.gtmEnv === 'production' && code === '501') {
  useHead({
    script: [
      {
        type: 'text/javascript',
        innerHTML: `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-NRN3VK5');`,
      },
    ],
    noscript: [
      {
        innerHTML: `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-NRN3VK5" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
      },
    ],
  });
}
</script>
